import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FocusedInputShape, isInclusivelyBeforeDay } from 'react-dates'
import CalendarDayPickerRange from 'src/components/CalendarDayPickerRange/CalendarDayPickerRange'
import DateTextInputField from 'src/components/DateTextInputField/DateTextInputField'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import { useDropdownOpen } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

interface DateRangePickerFieldsDropdownProps {
  id?: string
  since: Moment
  until: Moment
  setSince: (val: Moment | undefined) => void
  setUntil: (val: Moment | undefined) => void
  onProcessDatesChange?: (
    since: Moment | undefined,
    until: Moment | undefined,
  ) => { since: Moment | undefined; until: Moment | undefined }
  onApplyDates?: (since: Moment | undefined, until: Moment | undefined) => void
  onDropdownClose?: () => void
  sinceLabel?: React.ReactNode
  untilLabel?: React.ReactNode
  sinceTooltipText?: React.ReactNode
  untilTooltipText?: React.ReactElement
  tooltipText?: React.ReactNode
  disabled?: boolean | 'since' | 'until'
  loading?: boolean
  initialMonth?: Moment
  isOutsideRange?: (day: moment.Moment) => boolean
  errorMessage?: string | React.ReactElement
  footerContent?: React.ReactElement
  minimumRangeSize?: number
  utc?: boolean
  openLeft?: boolean
  keepDropdownOpen?: boolean
}

const DateRangePickerFieldsDropdown: React.FC<
  DateRangePickerFieldsDropdownProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const { dropdownOpen, divRef, handleOpenerClick } = useDropdownOpen({})

  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [calendarStartDate, setCalendarStartDate] = useState<string>('')
  const [calendarEndDate, setCalendarEndDate] = useState<string>('')

  const disabled = useMemo((): boolean | 'startDate' | 'endDate' => {
    if (props.disabled === true) return true
    if (props.disabled === 'since') return 'startDate'
    if (props.disabled === 'until') return 'endDate'
    return false
  }, [props.disabled])

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  )

  const momentLocal = props.utc ? moment.utc : moment

  const isOutsideRange = useCallback(
    (day: moment.Moment): boolean => {
      if (props.loading === true || props.disabled === true) {
        return true
      } else if (props.isOutsideRange) {
        return props.isOutsideRange(day)
      } else {
        return !isInclusivelyBeforeDay(day, momentLocal())
      }
    },
    [momentLocal, props.disabled, props.isOutsideRange, props.loading],
  )

  const handleStartTextInputChange = useCallback(
    (date: string): void => {
      setStartDate(date)
      const parsedDate = momentLocal(date, 'll', true)
      if (parsedDate.isValid()) {
        setCalendarStartDate(parsedDate.format('ll'))
        if (!props.onApplyDates) {
          props.setSince(parsedDate.clone())
        }
      }
    },
    [momentLocal, props.onApplyDates, props.setSince],
  )

  const handleEndTextInputChange = useCallback(
    (date: string): void => {
      setEndDate(date)
      const parsedDate = momentLocal(date, 'll', true)
      if (parsedDate.isValid()) {
        setCalendarEndDate(parsedDate.format('ll'))
        if (!props.onApplyDates) {
          props.setUntil(parsedDate)
        }
      }
    },
    [momentLocal, props.onApplyDates, props.setUntil],
  )

  useEffect(() => {
    if (!dropdownOpen) props.onDropdownClose?.()
  }, [dropdownOpen])

  // Update fieldDate when filters are externally reset
  useEffect(() => {
    const startFormatted = props.since.format('ll')
    const endFormatted = props.until.format('ll')
    setStartDate(startFormatted)
    setEndDate(endFormatted)
    setCalendarStartDate(startFormatted)
    setCalendarEndDate(endFormatted)
  }, [props.since, props.until])

  const onDatesChange = useCallback(
    (arg: { startDate: Moment | null; endDate: Moment | null }): void => {
      let { startDate: start, endDate: end } = arg

      if (props.onProcessDatesChange) {
        const processed = props.onProcessDatesChange(
          start ?? undefined,
          end ?? undefined,
        )
        start = processed.since ?? null
        end = processed.until ?? null
      }

      setStartDate(start?.format('ll') || '')
      setEndDate(end?.format('ll') || '')
      setCalendarStartDate(start?.format('ll') || '')
      setCalendarEndDate(end?.format('ll') || '')

      if (!props.onApplyDates && start && end) {
        props.setSince(start.clone())
        props.setUntil(end.clone())
      }
    },
    [
      props.onApplyDates,
      props.onProcessDatesChange,
      props.setSince,
      props.setUntil,
    ],
  )

  const onFocusChange = useCallback(
    (focusedInput: FocusedInputShape | null): void => {
      if (!disabled) setFocusedInput(focusedInput)
    },
    [disabled],
  )

  const calendarMonthKey = useMemo((): string => {
    const monthSource = calendarStartDate || calendarEndDate
    if (!monthSource) return 'calendar-empty'
    const parsedDate = momentLocal(monthSource, 'll', true)
    return parsedDate.isValid()
      ? parsedDate.format('YYYY-MM')
      : 'calendar-invalid'
  }, [calendarEndDate, calendarStartDate, momentLocal])

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
      `}
    >
      <div
        id={props.id}
        ref={divRef}
        onClick={(e): void => {
          if (disabled !== true) {
            handleOpenerClick(e)
          }
        }}
        onKeyPress={(): void => undefined}
        role="button"
        tabIndex={0}
        css={css`
          display: flex;
          align-items: center;
          margin-top: 5px;
          cursor: ${disabled === true ? 'default' : 'pointer'};
          ${getFontCss(
            14,
            disabled === true
              ? styleConstants.colors.gray_darkest
              : themeConstants.colors.cta,
            styleConstants.fontFamilies.regular,
          )};
          justify-content: space-between;
          width: 100%;
        `}
      >
        <DateTextInputField
          label={props.sinceLabel ?? translate('Start')}
          placeholder={''}
          value={startDate}
          focus={focusedInput === 'startDate'}
          onClick={(): void => {
            setFocusedInput('startDate')
          }}
          onChange={(val: string): void => {
            setStartDate(val)
            const parsedDate = momentLocal(val, 'll', true)
            if (parsedDate.isValid()) {
              setCalendarStartDate(parsedDate.format('ll'))
            }
          }}
          onBlur={(val: string): void => handleStartTextInputChange(val)}
          disabled={
            props.loading === true ||
            disabled === true ||
            disabled === 'startDate'
          }
          tooltipText={props.sinceTooltipText}
        />
        <div
          css={css`
            display: flex;
            align-self: flex-end;
            margin-bottom: 12px;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {'-'}
        </div>
        <DateTextInputField
          label={props.untilLabel ?? translate('End')}
          placeholder={''}
          value={endDate}
          focus={focusedInput === 'endDate'}
          onClick={(): void => {
            setFocusedInput('endDate')
          }}
          onChange={(val: string): void => {
            setEndDate(val)
            const parsedDate = momentLocal(val, 'll', true)
            if (parsedDate.isValid()) {
              setCalendarEndDate(parsedDate.format('ll'))
            }
          }}
          onBlur={(val: string): void => handleEndTextInputChange(val)}
          disabled={
            props.loading === true ||
            disabled === true ||
            disabled === 'endDate'
          }
          tooltipText={props.untilTooltipText}
        />
      </div>
      {dropdownOpen && (
        <div
          onClick={(e): void => {
            e.stopPropagation()
          }}
          onKeyDown={(): void => {
            undefined
          }}
          role="button"
          tabIndex={0}
          css={css`
            display: flex;
            flex-direction: column;
            position: absolute;
            ${props.openLeft
              ? css`
                  right: 0px;
                `
              : css`
                  left: 0px;
                `}
            z-index: 2;
            padding-top: 10px;
            border-radius: 10px;
            box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);
            background-color: ${styleConstants.colors.white};

            ${getBreakpointDown(breakpointSizes.XXSM)(css`
              left: -30px;
            `)}
          `}
        >
          <CalendarDayPickerRange
            key={calendarMonthKey}
            isOutsideRange={isOutsideRange}
            onDatesChange={onDatesChange}
            onFocusChange={onFocusChange}
            focusedInput={focusedInput}
            startDate={calendarStartDate}
            endDate={calendarEndDate}
            momentLocal={momentLocal}
          />
          {props.errorMessage && (
            <div
              css={css`
                margin-top: 20px;
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {props.errorMessage}
            </div>
          )}
          {props.footerContent && (
            <>
              <DividerLineDiv
                css={css`
                  margin: 20px 0 0 0;
                `}
              />
              <div
                css={css`
                  padding-top: 20px;
                `}
              >
                {props.footerContent}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default DateRangePickerFieldsDropdown
